import React, { useEffect, useState, useCallback } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users as UsersIcon, Plus, X, Search, Filter, Shield, KeyRound,
  Trash2, Edit3, UserCheck, UserX, Building2, Copy, Check,
} from "lucide-react";

const ROLE_OPTIONS = [
  { v: "all", l: "Todos" },
  { v: "alumno", l: "Alumnos" },
  { v: "partner_admin", l: "Partner admin" },
  { v: "instructor", l: "Instructor" },
  { v: "staff_dcc", l: "Staff DCC" },
  { v: "superadmin_dcc", l: "Superadmin" },
];

const ROLE_COLORS = {
  alumno: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  partner_admin: "bg-brand-orange-500/20 text-brand-orange-500 border-brand-orange-500/40",
  instructor: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  staff_dcc: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  superadmin_dcc: "bg-red-500/20 text-red-300 border-red-500/40",
  reseller: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  influencer: "bg-pink-500/20 text-pink-300 border-pink-500/40",
};

function RoleBadge({ role }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold rounded border ${ROLE_COLORS[role] || "bg-gray-700/40 text-gray-400 border-gray-700"}`}>
      {role.replace(/_/g, " ")}
    </span>
  );
}

export default function SuperadminUsers() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [tenantFilter, setTenantFilter] = useState("");
  const [tenants, setTenants] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (search) params.set("search", search);
      if (roleFilter && roleFilter !== "all") params.set("role", roleFilter);
      if (tenantFilter) params.set("tenant", tenantFilter);
      const { data } = await api.get(`/admin/users2?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setLoading(false); }
  }, [search, roleFilter, tenantFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/tenants").then((r) => setTenants(r.data.items || [])).catch(() => {});
  }, []);

  const resetPwd = async (userId) => {
    try {
      const r = await api.post(`/admin/users2/${userId}/reset-password`);
      toast.success(`Password temporal: ${r.data.temp_password}`, { duration: 15000 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users2/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Deshabilitado" : "Habilitado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">Fase 14.2 · Users management</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display flex items-center gap-2">
              <UsersIcon className="w-7 h-7" /> Usuarios
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {total} usuarios · vista global multi-tenant
            </p>
          </div>
          <button onClick={() => setShowModal(true)}
            data-testid="user-new-btn"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold">
            <Plus className="w-4 h-4" /> Nuevo usuario
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4 p-4 bg-[#111827] border border-gray-800 rounded-xl">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input placeholder="Buscar por email o nombre…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="users-search-input"
              className="w-full pl-9 pr-3 py-2 bg-[#0B0F14] border border-gray-800 rounded-md text-sm text-white" />
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
            data-testid="users-filter-role"
            className="px-3 py-2 bg-[#0B0F14] border border-gray-800 rounded-md text-sm text-white">
            {ROLE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}
            data-testid="users-filter-tenant"
            className="px-3 py-2 bg-[#0B0F14] border border-gray-800 rounded-md text-sm text-white">
            <option value="">Todos los tenants</option>
            {tenants.map((t) => <option key={t.id} value={t.slug}>{t.name}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="users-table">
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">Sin resultados</div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#0B0F14] border-b border-gray-800">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Usuario</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Roles</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Tenants</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Estado</th>
                  <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {items.map((u) => (
                  <tr key={u.id}
                    data-testid={`user-row-${u.email}`}
                    className="hover:bg-[#0B0F14] transition-colors">
                    <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedUserId(u.id)}>
                      <div>
                        <div className="text-white font-semibold text-sm">{u.full_name}</div>
                        <div className="text-xs text-gray-500 font-mono">{u.email}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.roles || []).map((r) => <RoleBadge key={r} role={r} />)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.tenants || []).map((t) => (
                          <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border border-gray-700 text-gray-300 bg-gray-800/40 font-mono">
                            <Building2 className="w-3 h-3" />{t.slug}
                          </span>
                        ))}
                        {(u.tenants || []).length > 1 && (
                          <span className="text-[10px] text-brand-orange-500 font-bold uppercase tracking-widest">MULTI</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                          <UserCheck className="w-3 h-3" /> Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border bg-red-500/20 text-red-300 border-red-500/40">
                          <UserX className="w-3 h-3" /> Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => resetPwd(u.id)}
                          data-testid={`user-reset-password-btn-${u.email}`}
                          className="p-1.5 text-gray-400 hover:text-yellow-500 rounded" title="Reset password">
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button onClick={() => toggleActive(u)}
                          data-testid={`user-toggle-active-${u.email}`}
                          className="p-1.5 text-gray-400 hover:text-brand-orange-500 rounded" title={u.is_active ? "Deshabilitar" : "Habilitar"}>
                          {u.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setSelectedUserId(u.id)}
                          data-testid={`user-edit-${u.email}`}
                          className="p-1.5 text-gray-400 hover:text-brand-orange-500 rounded" title="Editar">
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showModal && <NewUserModal onClose={() => { setShowModal(false); load(); }} tenants={tenants} />}
      {selectedUserId && <UserDetailModal userId={selectedUserId} tenants={tenants} onClose={() => { setSelectedUserId(null); load(); }} />}
    </StaffLayout>
  );
}

function NewUserModal({ onClose, tenants }) {
  const [form, setForm] = useState({
    email: "", full_name: "", roles: ["alumno"],
    tenant_slugs: [], send_welcome_email: true, phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const toggleRole = (r) => {
    const has = form.roles.includes(r);
    setForm({ ...form, roles: has ? form.roles.filter((x) => x !== r) : [...form.roles, r] });
  };

  const toggleTenant = (slug) => {
    const has = form.tenant_slugs.includes(slug);
    setForm({ ...form, tenant_slugs: has ? form.tenant_slugs.filter((x) => x !== slug) : [...form.tenant_slugs, slug] });
  };

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users2", form);
      setResult(data);
      toast.success("Usuario creado");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4 my-8"
        data-testid="new-user-modal">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white font-display">Nuevo usuario</h3>
            <p className="text-xs text-gray-500 mt-1">Crea un usuario con roles y tenants asignados</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-400">Email *</label>
                <input required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                  data-testid="user-form-email"
                  className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-400">Nombre completo *</label>
                <input required value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="user-form-full-name"
                  className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400">Roles *</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="user-form-roles-select">
                {["alumno", "partner_admin", "instructor", "staff_dcc", "superadmin_dcc", "reseller", "influencer"].map((r) => (
                  <button type="button" key={r} onClick={() => toggleRole(r)}
                    data-testid={`user-form-role-${r}`}
                    className={`px-3 py-1.5 text-xs rounded-md border ${
                      form.roles.includes(r) ? "bg-brand-orange-600 text-white border-brand-orange-600"
                      : "text-gray-400 border-gray-700 hover:border-brand-orange-500"
                    }`}>{r.replace(/_/g, " ")}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400">Tenants asignados</label>
              <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto" data-testid="user-form-tenants-select">
                {tenants.map((t) => (
                  <button type="button" key={t.slug} onClick={() => toggleTenant(t.slug)}
                    data-testid={`user-form-tenant-${t.slug}`}
                    className={`px-3 py-1.5 text-xs rounded-md border inline-flex items-center gap-1 ${
                      form.tenant_slugs.includes(t.slug) ? "bg-brand-orange-600 text-white border-brand-orange-600"
                      : "text-gray-400 border-gray-700 hover:border-brand-orange-500"
                    }`}>
                    <Building2 className="w-3 h-3" /> {t.name}
                  </button>
                ))}
              </div>
              {form.tenant_slugs.length > 1 && (
                <p className="mt-2 text-[10px] text-brand-orange-500 font-semibold">
                  Multi-tenant: este usuario podrá cambiar entre {form.tenant_slugs.length} tenants desde su topbar.
                </p>
              )}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.send_welcome_email}
                onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })}
                data-testid="user-form-send-welcome"
                className="w-4 h-4 accent-brand-orange-600" />
              <span className="text-sm text-gray-300">Enviar email de bienvenida <span className="text-brand-orange-500 text-xs">(MOCKED)</span></span>
            </label>
            <button type="submit" disabled={submitting}
              data-testid="user-form-submit"
              className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
              {submitting ? "Creando…" : "Crear usuario"}
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/40 rounded-lg">
              <p className="text-emerald-300 font-semibold text-sm">✓ Usuario creado</p>
            </div>
            <div className="p-4 bg-[#0B0F14] border border-gray-700 rounded-lg space-y-2">
              <div className="text-sm font-mono text-white">Email: {result.email}</div>
              <div className="flex items-center gap-2">
                <div className="text-sm font-mono text-brand-orange-500 flex-1">Password: {result.temp_password}</div>
                <button onClick={() => { navigator.clipboard.writeText(result.temp_password); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  className="p-1.5 rounded border border-gray-700 hover:border-brand-orange-500 text-gray-400 hover:text-brand-orange-500">
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button onClick={onClose} className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold">Cerrar</button>
          </div>
        )}
      </div>
    </div>
  );
}

function UserDetailModal({ userId, tenants, onClose }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("info");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [activity, setActivity] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users2/${userId}`);
      setDetail(data);
      setForm({
        full_name: data.full_name,
        roles: [...(data.roles || [])],
        tenant_slugs: (data.tenants || []).map((t) => t.slug),
        is_active: data.is_active,
        phone: data.phone || "",
      });
      const act = await api.get(`/admin/users2/${userId}/activity?limit=20`);
      setActivity(act.data.items || []);
    } catch (e) { toast.error("Error al cargar"); onClose(); }
  }, [userId, onClose]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users2/${userId}`, form);
      toast.success("Guardado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleTenantChip = (slug) => {
    const has = (form.tenant_slugs || []).includes(slug);
    setForm({ ...form, tenant_slugs: has ? form.tenant_slugs.filter((x) => x !== slug) : [...(form.tenant_slugs || []), slug] });
  };

  if (!detail) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-[#111827] border border-gray-800 rounded-xl overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="user-detail-modal">
        <div className="p-6 border-b border-gray-800 flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-white font-display">{detail.full_name}</h3>
            <p className="text-xs text-gray-500 font-mono">{detail.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div className="px-6 border-b border-gray-800 flex gap-4">
          {["info", "tenants", "activity"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              data-testid={`user-detail-tab-${t}`}
              className={`py-3 text-sm font-semibold border-b-2 ${
                tab === t ? "border-brand-orange-600 text-white" : "border-transparent text-gray-500 hover:text-gray-300"
              }`}>
              {t === "info" ? "Info" : t === "tenants" ? `Tenants (${(form.tenant_slugs || []).length})` : "Actividad"}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {tab === "info" && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-400">Nombre completo</label>
                <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-400">Roles</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["alumno", "partner_admin", "instructor", "staff_dcc", "superadmin_dcc"].map((r) => (
                    <button type="button" key={r} onClick={() => {
                      const has = form.roles.includes(r);
                      setForm({ ...form, roles: has ? form.roles.filter((x) => x !== r) : [...form.roles, r] });
                    }}
                      className={`px-3 py-1.5 text-xs rounded-md border ${
                        form.roles.includes(r) ? "bg-brand-orange-600 text-white border-brand-orange-600"
                        : "text-gray-400 border-gray-700"
                      }`}>{r.replace(/_/g, " ")}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 accent-brand-orange-600" />
                <span className="text-sm text-gray-300">Usuario activo</span>
              </label>
              <button onClick={save} disabled={saving}
                data-testid="user-detail-save"
                className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
                {saving ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          )}
          {tab === "tenants" && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {(form.tenant_slugs || []).map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-brand-orange-500/40 bg-brand-orange-500/10 text-brand-orange-500 text-xs font-mono">
                    <Building2 className="w-3 h-3" /> {s}
                    <button onClick={() => toggleTenantChip(s)} className="ml-1 hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="pt-3 border-t border-gray-800">
                <p className="text-[11px] uppercase tracking-widest text-gray-400 mb-2">Agregar tenant</p>
                <div className="flex flex-wrap gap-2">
                  {tenants.filter((t) => !(form.tenant_slugs || []).includes(t.slug)).map((t) => (
                    <button key={t.slug} onClick={() => toggleTenantChip(t.slug)}
                      className="px-3 py-1.5 text-xs rounded-md border border-gray-700 text-gray-400 hover:border-brand-orange-500 hover:text-brand-orange-500 inline-flex items-center gap-1">
                      <Plus className="w-3 h-3" /> {t.name}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={save} disabled={saving}
                className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold">
                Guardar tenants
              </button>
            </div>
          )}
          {tab === "activity" && (
            <div className="space-y-2">
              {activity.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">Sin actividad reciente</p>
              ) : activity.map((a) => (
                <div key={a.id} className="p-3 bg-[#0B0F14] border border-gray-800 rounded text-xs">
                  <div className="flex justify-between">
                    <span className="text-brand-orange-500 font-mono">{a.action}</span>
                    <span className="text-gray-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</span>
                  </div>
                  <div className="text-gray-400 mt-1">
                    resource: {a.resource_type}/{a.resource_id?.slice(0, 8) || "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
